import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { clearEmployeeSession } from "../../../store/employeeAuthSlice";
import { getEmployeeMeApi, EmployeeMe } from "../api/meApi";
import { getMyAdjustmentsApi, getMySignaturePendingApi, MySignaturePending } from "../api/timekeepingApi";
import SignTimesheetSheet from "../components/SignTimesheetSheet";
import EmployeeShell, { initialsOf } from "../components/EmployeeShell";
import {
  COLABORADOR_AFASTAMENTOS,
  COLABORADOR_AJUSTES,
  COLABORADOR_CONTRACHEQUES,
  COLABORADOR_COMPROVANTES,
  COLABORADOR_ESCALA,
  COLABORADOR_ESPELHO,
  COLABORADOR_FERIAS,
  COLABORADOR_LOGIN,
  COLABORADOR_SENHA,
} from "../routes";

import "./Colaborador.css";
import "./Ponto.css";

const STATUS_LABELS: Record<EmployeeMe["status"], string> = {
  ATIVO: "Ativo",
  AFASTADO: "Afastado",
  FERIAS: "Férias",
  DESLIGADO: "Desligado",
};

/**
 * Data civil (`@db.Date`) chega em ISO com meia-noite UTC. Sem `timeZone: "UTC"`
 * o navegador em America/Sao_Paulo (UTC-3) exibiria o dia ANTERIOR.
 */
const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("pt-BR", { timeZone: "UTC" });
};

/** "Bom dia" / "Boa tarde" / "Boa noite" pelo relógio do aparelho. */
const greetingNow = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Bom dia";
  if (hour < 18) return "Boa tarde";
  return "Boa noite";
};

/** "segunda-feira, 21 de setembro" — hoje, no fuso do aparelho. */
const todayLabel = () =>
  new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" });

const firstNameOf = (name?: string | null) => String(name ?? "").trim().split(/\s+/)[0] || "";

// ── Ícones (inline SVG, traço 2px — o mesmo estilo dos cards do staff) ──────
const svgProps = {
  width: 22,
  height: 22,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

const IconClock = () => (
  <svg {...svgProps} width={26} height={26}>
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);
const IconSheet = () => (
  <svg {...svgProps}>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <line x1="8" y1="8" x2="16" y2="8" />
    <line x1="8" y1="12" x2="16" y2="12" />
    <line x1="8" y1="16" x2="12" y2="16" />
  </svg>
);
const IconMoney = () => (
  <svg {...svgProps}>
    <rect x="3" y="6" width="18" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.5" />
    <line x1="6" y1="9" x2="6" y2="9.01" />
    <line x1="18" y1="15" x2="18" y2="15.01" />
  </svg>
);
const IconSun = () => (
  <svg {...svgProps}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);
const IconPause = () => (
  <svg {...svgProps}>
    <circle cx="12" cy="12" r="9" />
    <line x1="10" y1="9" x2="10" y2="15" />
    <line x1="14" y1="9" x2="14" y2="15" />
  </svg>
);
const IconKey = () => (
  <svg {...svgProps}>
    <circle cx="8" cy="15" r="4" />
    <path d="M10.85 12.15 19 4M18 5l2 2M15 8l2 2" />
  </svg>
);
const IconBuilding = () => (
  <svg {...svgProps} width={18} height={18}>
    <rect x="4" y="3" width="16" height="18" rx="1" />
    <path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2M10 21v-3h4v3" />
  </svg>
);
const IconBriefcase = () => (
  <svg {...svgProps} width={18} height={18}>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18" />
  </svg>
);
const IconPin = () => (
  <svg {...svgProps} width={18} height={18}>
    <path d="M12 21s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
);
const IconCalendar = () => (
  <svg {...svgProps} width={18} height={18}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M8 3v4M16 3v4M3 10h18" />
  </svg>
);
const IconId = () => (
  <svg {...svgProps} width={18} height={18}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="9" cy="12" r="2.5" />
    <path d="M14 10h4M14 14h4M5.5 17c.7-1.5 2-2.3 3.5-2.3s2.8.8 3.5 2.3" />
  </svg>
);

/**
 * Atalhos do portal. Sem `to`, o atalho aparece desabilitado ("Em breve") — o
 * colaborador já vê o que o app vai fazer, e o DP sabe o que ainda não pode
 * prometer.
 */
const TILES: { id: string; nome: string; dica: string; icon: React.ReactNode; to?: string }[] = [
  { id: "espelho", nome: "Meu espelho", dica: "Horas do mês e correções", icon: <IconSheet />, to: COLABORADOR_ESPELHO() },
  { id: "comprovantes", nome: "Meus comprovantes", dica: "Comprovante de cada marcação do ponto", icon: <IconSheet />, to: COLABORADOR_COMPROVANTES() },
  { id: "ajustes", nome: "Meus pedidos", dica: "Correções do ponto e a resposta do DP", icon: <IconSheet />, to: COLABORADOR_AJUSTES() },
  { id: "escala", nome: "Minha escala", dica: "Dias de trabalho e folgas do mês", icon: <IconCalendar />, to: COLABORADOR_ESCALA() },
  { id: "contracheques", nome: "Contracheques", dica: "Demonstrativos dos meses fechados", icon: <IconMoney />, to: COLABORADOR_CONTRACHEQUES() },
  { id: "ferias", nome: "Férias", dica: "Períodos e saldo", icon: <IconSun />, to: COLABORADOR_FERIAS() },
  { id: "afastamentos", nome: "Afastamentos", dica: "Atestados e licenças", icon: <IconPause />, to: COLABORADOR_AFASTAMENTOS() },
];

const monthLabel = (year: number, month: number) => new Date(year, month - 1, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

export default function EmployeeHomePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [me, setMe] = useState<EmployeeMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingAdjustments, setPendingAdjustments] = useState(0);
  const [toSign, setToSign] = useState<MySignaturePending[]>([]);
  const [signingAll, setSigningAll] = useState(false);

  const loadToSign = useCallback(() => {
    getMySignaturePendingApi()
      .then(setToSign)
      .catch(() => setToSign([]));
  }, []);

  useEffect(() => {
    loadToSign();
  }, [loadToSign]);

  const loadPending = useCallback(async () => {
    try {
      setPendingAdjustments((await getMyAdjustmentsApi("PENDENTE")).length);
    } catch {
      setPendingAdjustments(0);
    }
  }, []);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      setMe(await getEmployeeMeApi());
    } catch (error: any) {
      // Token recusado (credencial desativada, senha trocada em outro
      // dispositivo, sessão expirada): a sessão local não vale mais.
      toast.error(error?.message || "Sua sessão expirou. Entre novamente.");
      dispatch(clearEmployeeSession());
      navigate(COLABORADOR_LOGIN(), { replace: true });
    } finally {
      setLoading(false);
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const contrato = me?.currentContract ?? null;
  const empresa = contrato?.employer.tradeName || contrato?.employer.legalName || "—";
  const cargo = contrato?.position?.name || "—";
  const local = contrato?.store?.nomeFantasia || contrato?.costCenter?.name || "—";

  return (
    <EmployeeShell displayName={me?.displayName}>
      {loading ? (
        <p className="colabLoading">Carregando...</p>
      ) : me ? (
        <>
          {/* ── Hero: saudação ─────────────────────────────────────────────── */}
          <section className="colabHero">
            <div className="colabHero__top">
              <div className="colabAvatar colabAvatar--lg" aria-hidden="true">
                {initialsOf(me.displayName)}
              </div>
              <div>
                <h1 className="colabHero__greeting">
                  {greetingNow()}, {firstNameOf(me.displayName)}!
                </h1>
                <p className="colabHero__date">{todayLabel()}</p>
              </div>
              <span className="colabHero__badge">{STATUS_LABELS[me.status]}</span>
            </div>

            {me.status !== "DESLIGADO" ? (
              <p className="colabHero__note">
                <IconClock /> O ponto é marcado no aparelho da loja, pelo rosto.
              </p>
            ) : null}
          </section>

          {toSign.length >= 2 ? (
            <p className="colabNotice colabNotice--warn colabSignBanner">
              <span>Você tem {toSign.length} espelhos esperando a sua assinatura.</span>
              <button type="button" className="colabBtn colabBtn--primary colabBtn--compact" onClick={() => setSigningAll(true)}>
                Assinar todos ({toSign.length})
              </button>
            </p>
          ) : null}

          {signingAll ? (
            <SignTimesheetSheet
              mode="ASSINAR_VARIOS"
              months={toSign.map(({ year, month }) => ({ year, month }))}
              onClose={() => setSigningAll(false)}
              onDone={() => {
                setSigningAll(false);
                loadToSign();
              }}
            />
          ) : null}

          {toSign.map((item) => (
            <p key={`${item.year}-${item.month}`} className="colabNotice colabNotice--warn colabSignBanner">
              <span>
                {item.status === "DESATUALIZADO"
                  ? `Seu espelho de ${monthLabel(item.year, item.month)} mudou depois da sua assinatura — confira e assine de novo.`
                  : `Seu espelho de ${monthLabel(item.year, item.month)} está pronto — confira e assine.`}
              </span>
              <button
                type="button"
                className="colabBtn colabBtn--primary colabBtn--compact"
                onClick={() => navigate(`${COLABORADOR_ESPELHO()}?ano=${item.year}&mes=${item.month}`)}
              >
                Conferir
              </button>
            </p>
          ))}

          <div className="colabGrid">
            {/* ── Identidade ───────────────────────────────────────────────── */}
            <section className="colabPanel">
              <h2 className="colabPanel__title">Meus dados</h2>

              <div className="colabIdentity">
                <div className="colabAvatar colabAvatar--lg" aria-hidden="true">
                  {initialsOf(me.displayName)}
                </div>
                <div>
                  <p className="colabIdentity__name">{me.fullName}</p>
                  <p className="colabIdentity__role">{cargo}</p>
                </div>
              </div>

              <div className="colabInfoList">
                <div className="colabInfo">
                  <span className="colabInfo__icon"><IconId /></span>
                  <div>
                    <div className="colabInfo__label">CPF</div>
                    <div className="colabInfo__value">{me.cpfMasked || "—"}</div>
                  </div>
                </div>
                <div className="colabInfo">
                  <span className="colabInfo__icon"><IconBuilding /></span>
                  <div>
                    <div className="colabInfo__label">Empresa</div>
                    <div className="colabInfo__value">{empresa}</div>
                  </div>
                </div>
                <div className="colabInfo">
                  <span className="colabInfo__icon"><IconBriefcase /></span>
                  <div>
                    <div className="colabInfo__label">Cargo</div>
                    <div className="colabInfo__value">{cargo}</div>
                  </div>
                </div>
                <div className="colabInfo">
                  <span className="colabInfo__icon"><IconPin /></span>
                  <div>
                    <div className="colabInfo__label">Local</div>
                    <div className="colabInfo__value">{local}</div>
                  </div>
                </div>
                <div className="colabInfo">
                  <span className="colabInfo__icon"><IconCalendar /></span>
                  <div>
                    <div className="colabInfo__label">Admissão</div>
                    <div className="colabInfo__value">{formatDate(contrato?.admissionDate)}</div>
                  </div>
                </div>
              </div>
            </section>

            {/* ── Atalhos ──────────────────────────────────────────────────── */}
            <section className="colabPanel">
              <h2 className="colabPanel__title">O que dá para fazer aqui</h2>

              <div className="colabTiles">
                {TILES.map((tile) => (
                  <button
                    key={tile.id}
                    type="button"
                    className="colabTile"
                    disabled={!tile.to}
                    onClick={tile.to ? () => navigate(tile.to as string) : undefined}
                  >
                    {!tile.to ? <span className="colabTile__soon">Em breve</span> : null}
                    {tile.id === "ajustes" && pendingAdjustments ? (
                      <span className="colabTile__soon">{pendingAdjustments} aguardando</span>
                    ) : null}
                    <span className="colabTile__icon">{tile.icon}</span>
                    <span className="colabTile__name">{tile.nome}</span>
                    <span className="colabTile__hint">{tile.dica}</span>
                  </button>
                ))}

                <button
                  type="button"
                  className="colabTile"
                  onClick={() => navigate(COLABORADOR_SENHA())}
                >
                  <span className="colabTile__icon"><IconKey /></span>
                  <span className="colabTile__name">Alterar senha</span>
                  <span className="colabTile__hint">Troque quando quiser</span>
                </button>
              </div>

              <p className="colabFooterNote">
                Dúvidas sobre seus dados ou seu acesso? Procure o Departamento Pessoal.
              </p>
            </section>
          </div>
        </>
      ) : null}
    </EmployeeShell>
  );
}
